import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Form from "@ROM-ui/Form";
import PointerSpan from "@ROM-ui/PointerSpan";
import useSavedViews from "./useSavedViews";

const SavedViewsTabs = ({ filters, setFilter, initialFilters, storageKey }) => {
  const { views, activeViewId, addView, updateView, deleteView, setActiveView, reorderViews } = useSavedViews({
    storageKey,
    filters,
    initialFilters,
    setFilter,
  });

  const [editingViewId, setEditingViewId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [newViewName, setNewViewName] = useState("");

  const handleSaveView = () => {
    const name = newViewName.trim();
    if (!name) return;

    addView(name);
    setShowSaveInput(false);
    setNewViewName("");
  };

  const handleUpdateView = (id) => {
    const name = editingName.trim();
    if (!name) return;

    updateView(id, name);
    setEditingViewId(null);
    setEditingName("");
  };

  const handleDeleteView = (id, event) => {
    event.stopPropagation();
    deleteView(id);
  };

  const handleTabClick = (id) => {
    setEditingViewId(null);
    setActiveView(id);
  };

  const handleNameClick = (id, name, event) => {
    event.stopPropagation();

    if (id === activeViewId) {
      setEditingViewId(id);
      setEditingName(name);
    } else {
      handleTabClick(id);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const reordered = [...views];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    reorderViews(reordered);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="saved-views-tabs" direction="horizontal">
        {(droppableProvided) => (
          <TabsWrapper
            className="d-flex align-items-center flex-wrap mb-3"
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {views.map((view, index) => (
              <Draggable key={view.id} draggableId={view.id} index={index}>
                {(draggableProvided, snapshot) => (
                  <Tab
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    className={`d-flex align-items-center me-2 mb-2 px-3 py-2 ${view.id === activeViewId ? "active" : ""} ${
                      snapshot.isDragging ? "dragging" : ""
                    }`}
                    onClick={() => handleTabClick(view.id)}
                  >
                    <PointerSpan {...draggableProvided.dragHandleProps} className="fa fa-ellipsis-v text-muted me-2" />
                    {editingViewId === view.id ? (
                      <div role="presentation" className="d-flex align-items-center" onClick={(event) => event.stopPropagation()}>
                        <Form.Control
                          size="sm"
                          autoFocus
                          value={editingName}
                          onChange={(event) => setEditingName(event.target.value)}
                          onKeyDown={(event) => event.key === "Enter" && handleUpdateView(view.id)}
                          className="me-1"
                          style={{ width: "120px" }}
                        />
                        <PointerSpan
                          className="fa fa-check text-success me-2"
                          onClick={() => handleUpdateView(view.id)}
                          title="Save"
                        />
                        <PointerSpan className="fa fa-times text-danger" onClick={() => setEditingViewId(null)} title="Cancel" />
                      </div>
                    ) : (
                      <>
                        <span
                          role="button"
                          tabIndex={0}
                          className="text-nowrap"
                          onClick={(event) => handleNameClick(view.id, view.name, event)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") handleNameClick(view.id, view.name, event);
                          }}
                          title={view.id === activeViewId ? "Click to rename" : ""}
                        >
                          {view.name}
                        </span>
                        <PointerSpan
                          className="fa fa-times ms-2 opacity-75"
                          onClick={(event) => handleDeleteView(view.id, event)}
                          title="Delete view"
                        />
                      </>
                    )}
                  </Tab>
                )}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
            {showSaveInput ? (
              <div className="d-flex align-items-center px-2 py-1 mb-2">
                <Form.Control
                  size="sm"
                  autoFocus
                  placeholder="View name"
                  value={newViewName}
                  onChange={(event) => setNewViewName(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && handleSaveView()}
                  className="me-1"
                  style={{ width: "140px" }}
                />
                <PointerSpan className="fa fa-check text-success me-2" onClick={handleSaveView} title="Save view" />
                <PointerSpan
                  className="fa fa-times text-danger"
                  onClick={() => {
                    setShowSaveInput(false);
                    setNewViewName("");
                  }}
                  title="Cancel"
                />
              </div>
            ) : (
              <SaveButton className="text-nowrap mb-2" onClick={() => setShowSaveInput(true)}>
                <i className="fa fa-plus me-1" />
                Save View
              </SaveButton>
            )}
          </TabsWrapper>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default SavedViewsTabs;

SavedViewsTabs.propTypes = {
  filters: PropTypes.shape().isRequired,
  setFilter: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape().isRequired,
  storageKey: PropTypes.string.isRequired,
};

const TabsWrapper = styled.div``;

const Tab = styled.div`
  cursor: pointer;
  background-color: #f5f5f5;
  border-radius: 6px;
  font-size: 0.85rem;

  &:hover {
    background-color: #e9ecef;
  }

  &.active {
    background-color: var(--bs-primary);
    color: #fff;
  }

  &.dragging {
    opacity: 0.6;
  }
`;

const SaveButton = styled.div`
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border: 1px dashed #adb5bd;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #6c757d;

  &:hover {
    background-color: #f5f5f5;
  }
`;
